import {SurveyProductsSchema} from "../../../commons";
import {
  ExtendedConfiguration,
  PDFOutputSchema,
  PDFProductSchema,
  PDFServiceSchema,
  PDFSurveySchema,
  ServiceProcessedStep,
  PDFRecursiveQuestionIterationSchema,
  PDFWorksiteSchema,
  PDFWorksiteTeamMemberSchema,
  PDFWorksiteNoteSchema,
  PDFImageSchema,
  PDFDataSchema,
  PDFRecursiveQuestionSchema,
} from "../types";
import {AxiosInstance, AxiosResponse} from "axios";
import {
  ProductsWithIdSchema,
  ServiceConfigurationOutputVisibilityFlag,
  ServicesConfigurationsWithIdSchema,
  ServiceSurveyResponsesSchema,
  ServiceSurveysWithIdSchema,
  ServicesWithIdSchema,
  SystemsWithIdSchema,
  UserWithRoleSchema,
  WorksitesNotesWithIdSchema,
  WorksitesWithIdSchema,
} from "../../../types";
import prepareConfigurationStep from "@utils/prepareConfigurationStep";
import _ from "lodash";
import convertSurveyResponses from "@utils/convertSurveyResponses";
import filterActiveSteps from "@utils/filterActiveSteps";
import evaluateOutputCalculation from "@utils/evaluateOutputCalculation";
import retrieveKCCOptimizationResponses from "@utils/retrieveKCCOptimizationResponses";
import {stateToHTML} from "draft-js-export-html";
import {convertFromRaw} from "draft-js";
import moment from "moment";
import checkIfBlockIsVisible from "@utils/checkIfBlockIsVisible";
import {IntlShape} from "react-intl";
import {formatNumber} from "@feature/quotations/quotation-draft/lib/format";
import {getOptionTitle} from "@hooks/useGetOptionTitle";
import getKccResultContextKey from "@utils/getKccResultContextKey";
import {
  getRemoteSpreadsheetResults,
  getRemoteKColorResults,
} from "@feature/remote-calculation-results";
import evaluateJsonOutputValue from "../../../../utils/evaluateJsonOutputValue";
import {prepareCrossData} from "../../../../utils/prepareCrossData";
import {FIELDS_LIST} from "@feature/services/connettori";
import retrieveKColorResponses from "../../../../utils/retrieveKColorResponses";
import getKColorResultContextKey from "../../../../utils/getKColorResultContextKey";

export const getTotal = (data: PDFDataSchema[]) => data
  .reduce(
    (acc, {type, data}) => {
      if (type === "simple") {
        const output = data as PDFOutputSchema;
        return acc + (output.flg_subtotal ? (output.numeric_value as number | undefined) || 0 : 0);
      }
      else if (type === "recursive") {
        const iterations = data as PDFRecursiveQuestionSchema;
        return acc + iterations.reduce((accIn, {total}) => accIn + total, 0);
      }
      throw Error(`PDFDataSchema type ${type} not handled`);
    },
    0
  );

const retrieveSurveyEntities = async <T>(
  api: AxiosInstance,
  steps: ServiceProcessedStep[],
  baseEntities: {[pid: number]: T},
  target: 'products' | 'systems',
) => {
  const missingIds = steps
    .reduce(
      (acc, s) => _.uniq([...acc, ...s[target]]),
      [] as number[],
    )
    .filter((id) => !baseEntities[id]);
  let result = baseEntities;
  if (missingIds.length) {
    const missing: T[] = await api.get(
      `/${target}/all`,
      {params: {ids: missingIds.join(",")}},
    );
    result = {...baseEntities, ..._.keyBy(missing, 'id')};
  }
  return result || [];
}

const retrieveSurveyProducts = (
  api: AxiosInstance,
  steps: ServiceProcessedStep[],
  products: {[pid: number]: ProductsWithIdSchema},
) => retrieveSurveyEntities<ProductsWithIdSchema>(api, steps, products, 'products');

const retrieveSurveySystems = (
  api: AxiosInstance,
  steps: ServiceProcessedStep[],
  systems: {[pid: number]: SystemsWithIdSchema},
) => retrieveSurveyEntities<SystemsWithIdSchema>(api, steps, systems, 'systems');

const isBlockHidden = (blockExcludedRoles: string[] | undefined, role: string) =>
  !!blockExcludedRoles?.includes(`role_type_${role}`)

const allowBlock = (
  {type, hidden_question, flg_output_visibility}: ExtendedConfiguration,
  role: string,
) =>
  ['recursive_question', 'output'].includes(type || '')
  && !isBlockHidden(hidden_question, role)
  && (
    type === 'recursive_question'
    || (((flg_output_visibility || 0) & ServiceConfigurationOutputVisibilityFlag.PDF) > 0)
  );

const formatOutputValue = (
  value: number | string | boolean,
  output: ExtendedConfiguration,
) => {
  if (typeof value === "string")
    return value;
  if (typeof value === "boolean")
    return value ? "Si" : "No";
  if (Array.isArray(value)){
    // If value is an array, return it as a comma-separated string
    return value.map((v) => formatOutputValue(v, output)).join("\n");
  }
  const options = {
    maximumFractionDigits: output.output_ceil ? 0 : 2,
    ...(output.output_currency ? {style: "currency", currency: "EUR"} : {}),
  };
  return Intl.NumberFormat("it-IT", options).format(value);
}

type GetOutputValueReturnSchema = {
  numeric_value?: number,
  value: string | string[],
  value_params?: {[key: string]: string | null},
  value_params_type?: string,
}
const getOutputValue = (
  output: ExtendedConfiguration,
  context: any,
  recursiveQuestionRowIdx?: number,
): GetOutputValueReturnSchema | null | undefined => {
  // noinspection FallThroughInSwitchStatementJS
  switch (output.output_type) {
    case "calculation":
      const value = evaluateOutputCalculation(
        output,
        context,
        undefined,
        // @ts-ignore
        recursiveQuestionRowIdx,
        true,
      );
      const result: GetOutputValueReturnSchema = {
        numeric_value: value,
        value: formatOutputValue(value, output),
      };

      // handle async calculations
      if (output.calculation?.length === 1 && output.calculation[0].startsWith('{')) {
        const calculationType = JSON.parse(output.calculation[0]).original;
        if (!!calculationType) {
          switch (calculationType) {
            case 'kcc_optimization':
              // if calculation is a kcc optimization, add its parameters to the result
              const kccResponses: any = retrieveKCCOptimizationResponses(output, context.responses, context);
              const kccResultContextKey = getKccResultContextKey(kccResponses);
              const kccResult = context?.kccOptimizationResults?.[kccResultContextKey];
              result.value_params_type = "kcc_optimization";
              result.value_params = Object.entries(kccResult?.responses || {}).reduce(
                (acc, [key, value]) => ({
                  ...acc,
                  [key]: Array.isArray(value) ? value.join(", ") : value as string,
                }),
                {} as {[key: string]: string | null},
              );
              break;
            case 'kcolor':
              const {product, color, productPackage} = retrieveKColorResponses(context.responses, output, recursiveQuestionRowIdx);
              if (!product || !color || !productPackage)
                return null;
              const kColorResultContextKey = getKColorResultContextKey(product, color, productPackage);
              if (!kColorResultContextKey)
                return null;
              const kColorResult = context?.kColorResults?.[kColorResultContextKey];
              if (!kColorResult)
                return null;
              result.value_params_type = "kcolor";
              result.value_params = {
                color_code: kColorResult.kcolor_code,
                color_rgb: kColorResult.kcolor_rgb.join(","),
                product: kColorResult.product_des,
                package: kColorResult.min_qty + " " + kColorResult.um,
              };
              break;
            case 'spreadsheet':
            case 'connector':
              // if calculation is a remote spreadsheet or a connector, make the engine skip by returning null
              return null;
            case 'expression':
              break;
            case 'recursive_sum':
              return result;
            default:
              throw Error(`Async calculation type ${calculationType} not handled`);
          }
        }
      }
      return result;
    case "question":
      const response = context.responses[output.output_input_id!];
      if (!response)
        return undefined;

      // handle multi select response
      if (response.question?.input_type === "multi_select") {
        const options = context.configuration
          .filter((c: ServicesConfigurationsWithIdSchema) =>
            c.type === "option"
            && c.input_id == response.question.id
            && response.value.includes(c.id.toString())
          );
        if (!options.length)
          return undefined;
        return {
          value: options.map((o: ServicesConfigurationsWithIdSchema) => getOptionTitle(o)).join(", "),
          numeric_value: undefined,
        } as GetOutputValueReturnSchema;
      }

      if (response.question?.input_type === "node_reinforcement_input") {
        const field = output.metadata_?.field;
        if (!field)
          return undefined;
        const value = response.value?.[field];
        return {
          value: formatOutputValue(value, output),
          numeric_value: value,
        }
      }

      if (response.question?.input_type === "connector_input") {
        const field = output.metadata_?.field;
        if (!field)
          return undefined;

        const value = FIELDS_LIST[field]?.options
        ? FIELDS_LIST[field].options[response.value[field]]
        : response.value[field]

        return {
          value: formatOutputValue(value, output),
          numeric_value: value,
        }
      }

      if(response.question?.input_type === "k_color_input") {
        const field = output.metadata_?.field;
        if (!field)
          return undefined;
        let value: string | number | undefined;
        switch (field) {
          case "kColorProduct":
            value = response.value?.kColorProduct?.des_product || "";
            break;
          case "color":
            value = response.value?.color?.code || "";
            break;
          case "productPackage":
            value = response.value?.productPackage || "";
            break;
          default:
            value = undefined;
        }
        return {
          value: formatOutputValue(value || "", output),
          numeric_value: typeof value === "number" ? value : undefined,
        }
      }

      // handle other response types
      return {
        value: !!response.option ? getOptionTitle(response.option) : formatOutputValue(response.value, output),
        numeric_value: !response.option ? response.value : undefined,
      } as GetOutputValueReturnSchema;
    case "link":
      return {value: output.description, numeric_value: undefined} as GetOutputValueReturnSchema;
    case "text_image":
      return {value: "", numeric_value: undefined} as GetOutputValueReturnSchema;
    case "json_output_value":
      // @ts-ignore
      const v = evaluateJsonOutputValue(output, context, recursiveQuestionRowIdx) ?? null;
      return {
        numeric_value: v,
        value: formatOutputValue(v, output)
      };
    // @ts-ignore
    default:
      console.warn(`Output type ${output.output_type} not handled, returning null value (id=${output.id})`);
    // The following output types don't require value evaluation
    case "separator":
      return null
  }
};

/**
 * Get output type (specifically for PDF). Will return undefined if output type is not handled.
 */
const getPDFOutputType = (
  {id, type, output_type, output_input_id, metadata_}: ServicesConfigurationsWithIdSchema,
  blocks: ServicesConfigurationsWithIdSchema[],
): "number" | "link" | "text" | "boolean" | undefined => {

  if (type !== "output")
    throw Error(`Configuration ${id} is not an output`);

  switch (output_type) {
    case "calculation":
      return "number";
    case "json_output_value":
      return "number";
    case "question":
      const question = blocks
        .find((b) => b.id === output_input_id);
      if (!question)
        throw Error(`Question ${output_input_id} not found (output id: ${id})`);
      switch (question.input_type) {
        case "number_input":
        case "connector_input":
        case "node_reinforcement_input":
          return "number";
        case "bool":
          return "boolean";
        case "text_input":
        case "select":
        case "multi_select":
          return "text";
        case "k_color_input":
          switch(metadata_?.field) {
            case "kColorProduct":
              return "text";
            case "color":
              return "text";
            case "productPackage":
              return "number";
            default:
              return undefined;  // type not handled
          }
      }
      return undefined;  // type not handled
    case "link":
      return "link";
    case "text_image":
      return "text";
    default:
      return undefined;  // type not handled
  }
}

const buildPDFOutput = (
  intl: IntlShape,
  output: ExtendedConfiguration,
  context: any,
  parts?: PDFOutputSchema[],
  recursiveQuestionRowIdx?: number,
) => {
  const isProductsSubtotal = parts?.every((p) => p.value_2 !== undefined);
  return {
    type: getPDFOutputType(output, context.configuration),
    title: !isProductsSubtotal
      ? output.title
      : intl.formatMessage({id: "products", defaultMessage: "Prodotti"}),
    description: output.description,
    ...(getOutputValue(output, context, recursiveQuestionRowIdx) || {}),
    flg_subtotal: output.flg_subtotal,
    thickness: output.output_thickness,
    parts: parts,
  } as PDFOutputSchema
};

const sortBlocksFn = (a: ExtendedConfiguration, b: ExtendedConfiguration) => {
  if (!a.order && a.order !== 0) return 1;
  if (!b.order && b.order !== 0) return -1;
  return a.order - b.order;
}

const buildRecursiveQuestionRowContext = (row: any, recursiveQuestionId: number, context: any) => ({
  ...context,
  responses: {
    ..._.omit(context.responses, [recursiveQuestionId]),
    ...row,
  },
});

const extractCalculationOutputs = (
  calculation: string[] | undefined,
  outputs: ExtendedConfiguration[],
) => {
  if (!calculation) return [];
  return calculation
    .filter((p) => p.startsWith("{"))
    .map((p) => JSON.parse(p))
    .map((p) => outputs.find(({id}) => id === p.outputId))
    .filter((o) => !!o) as ExtendedConfiguration[];
};

type ExtractSubtotalPartsReturnType = {
  /** Dict of parts. Key is parent output ID, value is list of (already built) output parts */
  subtotalParts: Record<number, PDFOutputSchema[]>,
  /** Set of block IDs that have been encapsulated into another output (as a part of it) and must be excluded */
  excludedBlockIds: Set<number>
}
const extractSubtotalParts = (
  intl: IntlShape,
  surveyId: number,
  outputs: ExtendedConfiguration[],
  surveysProducts: SurveyProductsSchema | undefined,
  context: any,
  recursiveQuestionId?: number,
  recursiveQuestionRowIdx?: number,
): ExtractSubtotalPartsReturnType => {

  // 1. Define utilities
  const mergeResults = (
    old: ExtractSubtotalPartsReturnType,
    new_: ExtractSubtotalPartsReturnType,
  ) => ({
    subtotalParts: {...old.subtotalParts, ...new_.subtotalParts},
    excludedBlockIds: new Set([...Array.from(old.excludedBlockIds), ...Array.from(new_.excludedBlockIds)]),
  } as ExtractSubtotalPartsReturnType);
  const checkIfIsKColorRelatedCalculation = (part: any, context:any) => {
    const {outputId} = part;
    const output = context.configuration.find((c: ServicesConfigurationsWithIdSchema) => c.id === outputId);
    if (!output || output.output_type !== "calculation" || !output.calculation?.length)
      return null;
    if (output.calculation.length === 1 && output.calculation[0].startsWith("{")) {
      const calculationType = JSON.parse(output.calculation[0]).original;
      if (calculationType === "kcolor")
        return output;
    }
    return null;
  }
  const getProductIdFromCalculationPart = (part: any, context: any, recursiveResponseIndex:number|undefined) => {
    const {outputType, column} = part;
    const kColorOutput = checkIfIsKColorRelatedCalculation(part, context);
    if (kColorOutput) {
      const {product, color, productPackage}:any = retrieveKColorResponses(context.responses, kColorOutput);
      const key = getKColorResultContextKey(product, color, productPackage);
      if (key){
        return context?.kColorResults?.[key]?.product_id;
      }
      return undefined;
    }
    if (column !== "prezzo")
      return undefined;
    switch (outputType) {
      case "product":
        // CASE A: part is a fixed product
        const {product: {value: productId}} = part;
        return productId;
      case "product_question":
        // CASE B: part is a product selected in another question
        const {option: {product: {id: optionProductId}}} = context.responses[part.inputId];
        return optionProductId;
      default:
        return undefined;
    }
  }

  // 2. Perform extraction
  return outputs.reduce(
    (acc, b) => {
      if (!b.flg_subtotal || !b.calculation?.length)
        return acc;
      const excludedBlockIds = new Set<number>();
      const subtotalParts = {
        // First level parts
        [b.id]: extractCalculationOutputs(b.calculation, outputs)
          .sort(sortBlocksFn)
          .map((part) => {
            // Exclude part from further processing
            excludedBlockIds.add(part.id);
            // Build resulting part object
            const result = {
              ...buildPDFOutput(intl, part, context, undefined, recursiveQuestionRowIdx),
              id: part.id,
            } as PDFOutputSchema;
            // If part is a calculation with a product in it, retrieve its quantity
            const innerCalculationParts = part.calculation
              ?.filter((innerPart) => innerPart.startsWith("{"))
              ?.map((innerPart) => JSON.parse(innerPart))
              || [];
            const productId = innerCalculationParts.reduce(
              (acc, part) => acc || getProductIdFromCalculationPart(part, context, recursiveQuestionRowIdx),
              undefined as number | undefined,
            );
            if (!!productId) {
              // Exclude other outputs that are part of the same calculation
              innerCalculationParts
                .filter(({outputType}) => outputType === "output")
                .forEach(({outputId}) => {
                  if (checkIfIsKColorRelatedCalculation({outputId}, context))
                    return;  // Skip KColor calculations
                  excludedBlockIds.add(outputId);
                });
              // Retrieve product quantity
              const entry = Object.values(surveysProducts || {})
                .find((sp) => sp.product.id === productId);
              if (!!entry) {
                const sq = entry.splitQuantities[surveyId];
                const fraction= sq?
                  [recursiveQuestionId, recursiveQuestionRowIdx].every((v) => v !== undefined && v !== null)
                    ? sq.recursive_questions[recursiveQuestionId!][recursiveQuestionRowIdx!]
                    : sq.main
                  : 0;
                // Integrate product info in part (replace title with product name, add quantity)
                const {
                  product: {
                    art_des_cmpy,
                    art_des_it,
                    base_um,
                    prezzo: unitPrice,
                  },
                } = entry;
                result.title = art_des_cmpy || art_des_it || entry?.product?.product_des || result.title ;
                result.value_2 = !!unitPrice
                  ? `${formatNumber(
                    (result.numeric_value ?? fraction) / unitPrice, 
                      {maximumFractionDigits: 2})
                    }${(!!base_um ? ' [' + base_um.toLowerCase() + ']' : '')}`
                  : '';
              }
            }
            return result;
          })
      };
      return mergeResults(acc, {subtotalParts, excludedBlockIds} as ExtractSubtotalPartsReturnType);
    },
    {subtotalParts: {}, excludedBlockIds: new Set()} as ExtractSubtotalPartsReturnType
  )
}

const extractBlockOutputs = (
  intl: IntlShape,
  blocks: ExtendedConfiguration[],
  role: string,
  context: any,
  surveysProductsInfo: SurveyProductsSchema | undefined,
  surveyId: number,
  recursiveQuestionId?: number,
  recursiveQuestionRowIdx?: number,
) => {

  // 1. Extract subtotal parts
  const {subtotalParts, excludedBlockIds} = extractSubtotalParts(
    intl,
    surveyId,
    blocks.filter((b) => b.type === "output") || [],
    surveysProductsInfo,
    context,
    recursiveQuestionId,
    recursiveQuestionRowIdx,
  );

  // 2. Parse step outputs
  return blocks
    .filter((b) => !excludedBlockIds.has(b.id) && allowBlock(b, role))
    .filter((b) => {
      // added to avoid outputs that are flg_subtotal but have empty array of parts ticket H2532289916
      if (b.flg_subtotal)
        if (subtotalParts[b.id]?.length > 0)
          return true;
        else
          return false;
      return true;
    })
    .sort(sortBlocksFn)
    .map((b) => {
      // --- CASE A: simple output
      if (b.type !== "recursive_question")
        return {
          id: b.id,
          type: "simple",
          data: buildPDFOutput(intl, b, context, b.flg_subtotal ? subtotalParts[b.id] : undefined, recursiveQuestionRowIdx),
        } as PDFDataSchema;

      // --- CASE B: recursive question
      const recursiveQuestionResponse = context.responses?.[b.id];
      const recursiveQuestionRows: {}[] = recursiveQuestionResponse?.recursive_question_value || [];

      // 1. Evaluate rows outputs
      const rows = recursiveQuestionRows.map((row: any, idx: number) => {
        const rowContext = buildRecursiveQuestionRowContext(row, b.id, context)
        const outputs: PDFDataSchema[] = extractBlockOutputs(
          intl,
          b.blocks
            ?.filter((ib) =>
              // @ts-ignore
              checkIfBlockIsVisible(ib, rowContext, idx, false)
            )
            || [],
          role,
          rowContext,
          surveysProductsInfo,
          surveyId,
          b.id,
          idx,
        );
        return {
          total: getTotal(outputs),
          data: outputs.map(({data}) => data),
        } as PDFRecursiveQuestionIterationSchema;
      });

      // 2. Return recursive question data object
      return {
        id: b.id,
        type: "recursive",
        title: b.title,
        total: _.sum(rows.map(({total}) => total)),
        data: rows,
      } as PDFDataSchema;
    })
    .filter((o) =>
      o.type === "recursive"
      || (o.data as PDFOutputSchema).value !== undefined
    );
}

export const getSurveyOutputs = async (
  intl: IntlShape,
  api: AxiosInstance,
  survey: ServiceSurveysWithIdSchema,
  services: ServicesWithIdSchema[],
  products: {[pid: number]: ProductsWithIdSchema},
  systems: {[sid: number]: SystemsWithIdSchema},
  role: string,
  getServiceConfiguration: (serviceId: number, version: number) => Promise<AxiosResponse>,
  getCrossServicesConfiguration: (serviceId: number, version: number) => Promise<AxiosResponse>,
  surveysProductsInfo?: SurveyProductsSchema,
  targetPrice: "kerakoll" | "tintometer" = "kerakoll",
): Promise<PDFDataSchema[]> => {

  // 1. Retrieve survey's service data
  const service = services
    .find(({id}) => id === survey.service_id);
  if (!service)
    throw Error(`Service ${survey.service_id} not found`);

  const [
    responses,
    configuration,
    crossServicesConfiguration,
    kccOptimizationResults,
    spreadsheetResults,
    connectionResults,
    kColorResults,
  ] = await Promise.all([
    // --- survey responses promise
    api.get("/service_survey_responses/all", {
      params: { service_survey_id: survey.id },
    }) as Promise<ServiceSurveyResponsesSchema[]>,
    // --- service configurations promise
    getServiceConfiguration(
      service.id,
      survey.service_version
    ) as unknown as Promise<ServicesConfigurationsWithIdSchema[]>,
    // --- cross services configurations promise
    getCrossServicesConfiguration(
      service.id,
      survey.service_version,
    ) as unknown as Promise<ServicesConfigurationsWithIdSchema[]>,
    // --- kcc optimization results promises
    api
      .get(`/tintometers/optimization_result/${survey.id}`, {
        params: {
          tintometer_flg: service.type === "tintometer",
          include_responses: true,
        },
      })
      .then((res: any) =>
        res.reduce((prev: Record<string, any>, curr: any) => {
          const kccResultContextKey: string = getKccResultContextKey({
            color: curr.cod_color,
            system: curr.cod_system,
            surface: curr.val_surface,
            survey: curr.cod_survey,
            primers: curr.lst_primers,
            reti: curr.lst_reti,
            rasanti: curr.lst_rasanti,
            is_tintometer: curr.is_tintometrist_version,
          });
          return { ...prev, [kccResultContextKey]: curr };
        }, {} as Record<string, any>)
      ),
    // --- remote spreadsheet results promise
    getRemoteSpreadsheetResults(survey.id, api),
    // --- connector results promise
    api.get(`/connectors/${survey.id}`),
    getRemoteKColorResults(survey.id, api),
  ]);

  // 2. Prepare context
  const steps: ServiceProcessedStep[] = prepareConfigurationStep(configuration,undefined,service.id);
  const crossData = prepareCrossData(crossServicesConfiguration);

  const [
    surveyProducts,
    surveySystems,
  ] = await Promise.all([
    retrieveSurveyProducts(api, steps, products),
    retrieveSurveySystems(api, steps, systems),
  ]);

  const contextWithOutResponses = {
    survey,
    data: steps,
    configuration,
    products: surveyProducts,
    systems: surveySystems,
    outputs: steps.reduce((acc, step) => [
      ...acc,
      ...step.output,
    ], [] as ServicesConfigurationsWithIdSchema[]),
    responses: {},
    kccOptimizationResults,
    spreadsheetResults,
    kColorResults,
    connectionResults,
    context: "PDF",
    crossData,
    globalTargetPrice: targetPrice,
  };
  const convertedResponses = convertSurveyResponses(responses, contextWithOutResponses);
  const context = {...contextWithOutResponses, responses: convertedResponses};

  // 3. Evaluate steps outputs (visibility, calculations, ...)
  const serviceBlocksOrders = configuration
    .filter(b=>b.type==='service')
    .reduce((acc, b) => ({...acc, [b.id]: b.order}), {} as Record<number, number>);


  return filterActiveSteps(context)
    .filter((step: ServiceProcessedStep) => !isBlockHidden(step.hidden_question, role))
    .sort((a: ServiceProcessedStep, b: ServiceProcessedStep) => {
      const orderToUseA = a.service_block_id? serviceBlocksOrders[a.service_block_id] : a.order
      const orderToUseB = b.service_block_id? serviceBlocksOrders[b.service_block_id] : b.order
      if (orderToUseA === orderToUseB){
        return a.order - b.order;
      } else {
        return orderToUseA - orderToUseB;
      }
    })
    .reduce(
      (acc: PDFDataSchema[], step: ServiceProcessedStep) => [
        ...acc,
        ...extractBlockOutputs(
          intl,
          step.blocks || [],
          role,
          context,
          surveysProductsInfo,
          survey.id
        )
      ],
      [] as PDFDataSchema[],
    );
};

export const getSurveyData = async (
  intl: IntlShape,
  api: AxiosInstance,
  surveys: ServiceSurveysWithIdSchema[],
  services: ServicesWithIdSchema[],
  products: {[pid: number]: ProductsWithIdSchema},
  systems: {[sid: number]: SystemsWithIdSchema},
  role: string,
  getServiceConfiguration: (surveyId: number, version: number) => Promise<AxiosResponse>,
  getCrossServicesConfiguration: (surveyId: number, version: number) => Promise<AxiosResponse>,
  surveysProducts?: SurveyProductsSchema,
  targetPrice: "kerakoll" | "tintometer" = "kerakoll",
): Promise<PDFServiceSchema[]> => {

  // 1. Retrieve evaluated surveys outputs
  const surveysOutputs = await Promise.all(
    surveys.map((s) =>
      getSurveyOutputs(intl, api!, s, services, products, systems, role, getServiceConfiguration, getCrossServicesConfiguration, surveysProducts, targetPrice)
    )
  );
  const surveysOutputsDict = surveys.reduce(
    (acc, s, i) => ({
      ...acc,
      [s.id]: surveysOutputs[i]
    }),
    {} as {[sid: number]: any},
  )
  services.sort((s1,s2)=> s1.id - s2.id);
  // 2. Build PDF data object
  return services
    .filter(({id}) =>
      surveys.some((s) => s.service_id === id)
    )
    .reduce(
      (acc, service) => {
        const currentServiceSurveys = surveys
          .filter((s) => s.service_id === service.id);
        currentServiceSurveys.sort((s1, s2) => s1.id - s2.id);
        const currentServicesSurveysTotals = currentServiceSurveys
          .reduce(
            (acc, {id}) => ({
              ...acc,
              [id]: getTotal(surveysOutputsDict[id]),
            }),
            {} as {[sid: number]: number},
          );
        return [
          ...acc,
          {
            title: service.name,
            total: _.sum(Object.values(currentServicesSurveysTotals)),
            surveys: currentServiceSurveys.map(({id, name}) => ({
              title: name
                || intl.formatMessage({
                  id: "unnamed_configuration",
                  defaultMessage: "Configurazione senza nome",
                }),
              total: currentServicesSurveysTotals[id],
              data: surveysOutputsDict[id],
              id: id,
            }) as PDFSurveySchema)
          } as PDFServiceSchema,
        ];
      },
      [] as PDFServiceSchema[],
    )
}

export const buildWorksitePdfObj = (
  intl: IntlShape,
  worksite: WorksitesWithIdSchema,
  users: UserWithRoleSchema[]
) =>
  ({
    id: worksite.id,
    type: worksite.cod_worksite_type,
    construction_type: worksite.cod_worksite_construction,
    status: Object.values(worksite.cod_worksite_status || {}).reduce(
      (acc, { step, flg_active }) => (flg_active ? step : acc),
      ""
    ) as string,
    open_on: Object.values(worksite.cod_worksite_status || {}).find(
      ({ step }) => step === "open"
    )?.date,
    closed_on: Object.values(worksite.cod_worksite_status || {}).find(
      ({ step }) => step === "closed"
    )?.date,
    address: worksite.address,
    city: worksite.city,
    province: worksite.cod_province,
    zip_code: worksite.zip_code,
    latitude: worksite.latitude,
    longitude: worksite.longitude,
    team: users.map(
      (u) =>
        ({
          first_name: u.firstname,
          last_name: u.lastname,
          email: u.email,
          role:
            u.role.name === "contact_user"
              ? intl.formatMessage({id:u.account_user?.role.name})
              : intl.formatMessage({id:u.role.name}),
          internal: u.flg_internal,
        } as PDFWorksiteTeamMemberSchema)
    ),
  } as PDFWorksiteSchema);

export const buildWorksiteNotePdfObj = (note: WorksitesNotesWithIdSchema) => ({
  text: stateToHTML(convertFromRaw(note.val_contents as any)),
  created_at: moment(note.created_at).local().format("YYYY-MM-DD"),
  author: note.val_author,
} as PDFWorksiteNoteSchema)

export const buildPdfProducts = (products: SurveyProductsSchema | undefined) => {
  if (!products)
    return [];
  /*
  hubspot ticket https://app.hubspot.com/contacts/8760420/record/0-5/21845089189/
  In this ticket in a recursive question Geolite Gel A and Geolite Gel B and Geosteel G3300
  were present but not added to the products kpi because they evaluated to zero.

  This array is used by the backend to create the "Distinta materiali"
  and we want to keep only the products with quantity > 0 in the Distinta materiali.
  Other products are needed in the context for the survey data generation.
   */
  return Object.values(products).filter(
    ({quantity}) => quantity > 0
  ).map((p) => ({
    id: p.product.id,
    type: p.isKcolor ? "kcolor" : "standard",
    code: p.product.art_cod || p.product.code,
    name: p.product.art_des_cmpy || p.product.art_cod || p.product.product_des,
    min_qty: !p.isKcc ? p.product.min_qty : undefined,
    overall_qty: p.quantity,
    um: p.product.base_um,
    split_quantities: p.splitQuantities,
    technical_sheet_url: p.product.link_scheda_tecnica,
    link_image: p.product.link_image,
    metadata_: p.isKcolor
      ? {kcolor: {color: {code: p.kcolor_color_code, rgb: p.kcolor_color_rgb}}}
      : null,
    has_any_document: _.some([
      p.product.link_dop,
      p.product.link_cam,
      p.product.link_epd,
      p.product.link_sds,
      p.product.link_voc,
    ]),
  } as PDFProductSchema));
}

export const buildPdfImages = (
  list: {image_url: string, des_image: string}[] | undefined,
  urlsToKeep: string[],
): PDFImageSchema[] => {
  if (!list) return [];
  return list.filter(({image_url}) => urlsToKeep.includes(image_url));
}